import { getFullTopLeftLocation } from '../../utils/getLocation';

import { canStartGridDragFromEvent } from './dragInput';
import { getGridGuideArrowVisibility } from './gridGuideArrows';
import {
  HORIZONTAL_NUMBER_LINE_MODAL_HEIGHT,
  GRID_GUIDE_MODAL_MARGIN,
  getCoordinateGridGuideModalLayout,
  getGridGuideModalActionAtPoint,
  getNumberLineGridGuideModalLayout,
  isPointInGridGuideModal,
} from './gridGuideModalLayout';
import { paintGridGuideModal } from './gridGuideModalPainter';
import { revealGridGuideRegion } from './gridGuideVisibility';
import { createPressAndHoldRepeater } from './pressAndHoldRepeater';

const CLICK_SLIDE_DELTA = 2;

const distanceBetween = (first, second) =>
  Math.hypot(first.cx - second.cx, first.cy - second.cy);

export const createGridGuide = ({
  parabolaVertexThreshold,
  pointColor,
  getCanvasWidth,
  getCanvasHeight,
  getThemeColor,
  getThemeName,
  isSlopeNearlyVertical,
  isNearlyHorizontal,
  paintCanvas,
  changedMaybe,
  repositionProblemRows,
  getCanvasElement,
  setCanvasCursor,
}) => {
  const gridGuide = {
    ignoreNextGridMouseDown: false,
  };
  const arrowRepeater = createPressAndHoldRepeater();
  const activeActions = {};
  const axisAdjustment = parabolaVertexThreshold * 2;
  let arrowRepeatAction;
  let modalSpace;

  const isNumberLineGuide = () =>
    Boolean(gridGuide.grid && gridGuide.grid.args.nl);

  const getNumberLineModalLayout = () => {
    const grid = gridGuide.grid;
    const gridLocation = getFullTopLeftLocation(grid);
    const submitRowY =
      grid.getter &&
      grid.getter.submitRow &&
      getFullTopLeftLocation(grid.getter.submitRow).y;

    return getNumberLineGridGuideModalLayout({
      arrows: getGridGuideArrowVisibility(grid),
      canvasWidth: getCanvasWidth(),
      canvasHeight: getCanvasHeight(),
      grid: {
        x: gridLocation.x,
        y: gridLocation.y,
        w: grid.viewportW,
        h: grid.viewportH,
      },
      submitRowY,
    });
  };

  const getCoordinateModalLayout = () => {
    const grid = gridGuide.grid;
    const gridLocation = getFullTopLeftLocation(grid);

    return getCoordinateGridGuideModalLayout({
      canvasWidth: getCanvasWidth(),
      grid: {
        x: gridLocation.x,
        y: gridLocation.y,
        w: grid.viewportW,
        h: grid.viewportH,
      },
    });
  };

  gridGuide.getModalLayout = () =>
    isNumberLineGuide()
      ? getNumberLineModalLayout()
      : getCoordinateModalLayout();

  gridGuide.getModalAction = (x, y) =>
    getGridGuideModalActionAtPoint(gridGuide.getModalLayout(), x, y);

  gridGuide.stopGridGuideArrowRepeat = () => {
    const repeatAction = arrowRepeatAction;
    const wasRunning = arrowRepeater.isRunning();

    arrowRepeater.stop();
    arrowRepeatAction = undefined;

    if (repeatAction) {
      activeActions[repeatAction] = false;
    }

    return wasRunning;
  };

  const restoreModalSpace = () => {
    const previousModalSpace = modalSpace;

    if (!previousModalSpace) {
      return;
    }

    previousModalSpace.getter.setAllDim(
      previousModalSpace.getterW,
      previousModalSpace.getterH
    );
    modalSpace = undefined;
    repositionProblemRows();
  };

  gridGuide.close = () => {
    gridGuide.stopGridGuideArrowRepeat();
    gridGuide.on = false;
    setCanvasCursor('default');
    restoreModalSpace();
  };

  const prepareModalSpace = () => {
    if (modalSpace) {
      return;
    }

    const getter = gridGuide.grid && gridGuide.grid.getter;

    if (!getter) {
      return;
    }

    let requiredCanvasHeight;

    if (isNumberLineGuide()) {
      if (getGridGuideArrowVisibility(gridGuide.grid).up) {
        return;
      }

      const gridLocation = getFullTopLeftLocation(gridGuide.grid);
      const submitRowY =
        getter.submitRow && getFullTopLeftLocation(getter.submitRow).y;
      const desiredModalTop =
        submitRowY ?? gridLocation.y + gridGuide.grid.viewportH + 5;

      requiredCanvasHeight =
        desiredModalTop +
        HORIZONTAL_NUMBER_LINE_MODAL_HEIGHT +
        GRID_GUIDE_MODAL_MARGIN;
    } else {
      const modal = getCoordinateModalLayout().modal;

      requiredCanvasHeight = modal.y + modal.h + GRID_GUIDE_MODAL_MARGIN;
    }

    const overflow = Math.max(0, requiredCanvasHeight - getCanvasHeight());

    if (overflow === 0) {
      return;
    }

    modalSpace = {
      getter,
      getterH: getter.viewportH,
      getterW: getter.viewportW,
    };

    getter.setAllDim(getter.viewportW, getter.viewportH + overflow);
    repositionProblemRows();
  };

  gridGuide.open = (dot, grid) => {
    gridGuide.grid = grid;
    gridGuide.on = true;
    gridGuide.dot = dot;
    // Keep guide-arrow nudges grouped only while this guide selection is active.
    gridGuide.nudgeHistoryKey = {};
    prepareModalSpace();

    const revealGuide = () => {
      if (!gridGuide.on || gridGuide.dot !== dot) {
        return;
      }

      const canvas = getCanvasElement();

      if (canvas) {
        revealGridGuideRegion(canvas, gridGuide.getModalLayout().modal);
      }
    };

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(revealGuide);
    } else {
      setTimeout(revealGuide, 0);
    }
  };

  const invalidDestinationDot = dot => {
    if (
      gridGuide.currentObj &&
      gridGuide.currentObj.t === 'Parabola' &&
      gridGuide.currentObj.first &&
      gridGuide.currentObj.second &&
      dot
    ) {
      const cxDiff = Math.abs(gridGuide.currentObj.first.cx - dot.cx);
      const cyDiff = Math.abs(gridGuide.currentObj.first.cy - dot.cy);

      return (
        Math.round(cxDiff) <= parabolaVertexThreshold ||
        Math.round(cyDiff) <= parabolaVertexThreshold
      );
    }

    return false;
  };

  const checkForSnapToVertHor = (pre, post, adjusted) => {
    const grid = gridGuide.grid;

    if (!grid.currentObj || grid.currentObj === gridGuide.dot) {
      return;
    }

    const previousX = pre.cx;
    const previousY = pre.cy;
    const nextX = post.cx;
    const nextY = post.cy;

    if (
      Math.abs(nextX) < 10 &&
      ((previousX < 0 && nextX < 0) || (previousX > 0 && nextX > 0)) &&
      Math.abs(nextX) < Math.abs(previousX) &&
      !invalidDestinationDot({ cx: 0, cy: post.cy })
    ) {
      post.cx = 0;
    }

    if (
      Math.abs(nextY) < 10 &&
      ((previousY < 0 && nextY < 0) || (previousY > 0 && nextY > 0)) &&
      Math.abs(nextY) < Math.abs(previousY) &&
      !invalidDestinationDot({ cx: post.cx, cy: 0 })
    ) {
      post.cy = 0;
    }

    if (!grid.currentObj.first || grid.currentObj.first === gridGuide.dot) {
      return;
    }

    const first = grid.currentObj.first;

    if (distanceBetween(first, post) < 10) {
      return;
    }

    const wasAligned = first.cx === previousX || first.cy === previousY;
    const isAligned = first.cx === post.cx && first.cy === post.cy;

    if (wasAligned || isAligned) {
      return;
    }

    const previousSlope = (previousY - first.cy) / (previousX - first.cx);
    const nextSlope = (post.cy - first.cy) / (post.cx - first.cx);
    const crossedSlope =
      (previousSlope < 0 && nextSlope > 0) ||
      (previousSlope > 0 && nextSlope < 0);

    if (!adjusted && crossedSlope) {
      if (isSlopeNearlyVertical(nextSlope)) {
        post.cx = first.cx;
      } else if (isNearlyHorizontal(nextSlope)) {
        post.cy = first.cy;
      }
    }
  };

  const scheduleGridMouseDownReset = () => {
    setTimeout(() => {
      gridGuide.ignoreNextGridMouseDown = false;
    }, 500);
  };

  const closeFromModalInteraction = () => {
    gridGuide.close();
    gridGuide.ignoreNextGridMouseDown = true;
    scheduleGridMouseDownReset();

    return true;
  };

  const activateArrow = action => {
    activeActions[action] = true;

    setTimeout(() => {
      if (!arrowRepeater.isRunning()) {
        activeActions[action] = false;
      }

      paintCanvas();
    }, 400);
  };

  const nudgeDot = action => {
    const grid = gridGuide.grid;
    const axis = action === 'left' || action === 'right' ? 'cx' : 'cy';
    const direction = action === 'left' || action === 'down' ? -1 : 1;
    const delta = grid.args.stg ? grid.args.pixelsPerBlock : CLICK_SLIDE_DELTA;

    gridGuide.dot[axis] += direction * delta;

    const needsAdjusting = invalidDestinationDot(gridGuide.dot);

    if (needsAdjusting) {
      const adjustment = grid.args.stg ? delta : delta + axisAdjustment;

      gridGuide.dot[axis] += direction * adjustment;
    }

    activateArrow(action);

    return needsAdjusting;
  };

  const constrainDotToGrid = (
    previousX,
    previousY,
    arrowVisibility,
    needsAdjusting
  ) => {
    if (!gridGuide.dot) {
      return;
    }

    const grid = gridGuide.grid;

    checkForSnapToVertHor(
      { cx: previousX, cy: previousY },
      gridGuide.dot,
      needsAdjusting
    );

    if (
      (grid.getter && grid.getter.actingAsNumberLineGetter) ||
      isNumberLineGuide()
    ) {
      if (arrowVisibility.up) {
        gridGuide.dot.cx = previousX;
        gridGuide.dot.cy = Math.max(
          grid.bottomPixelY(),
          Math.min(gridGuide.dot.cy, grid.topPixelY())
        );
      } else {
        gridGuide.dot.cy = previousY;
        gridGuide.dot.cx = Math.max(
          grid.leftMostPixelX(),
          Math.min(gridGuide.dot.cx, grid.rightMostPixelX())
        );
      }

      return;
    }

    const nextX = gridGuide.dot.cx;
    const nextY = gridGuide.dot.cy;

    if (grid.args.eF) {
      const maximum = grid.viewportW - grid.viewportSlideX;

      if (nextX < 0 || nextY < 0 || nextX > maximum || nextY > maximum) {
        gridGuide.dot.cx = previousX;
        gridGuide.dot.cy = previousY;
      }

      return;
    }

    const maximum = grid.viewportW / 2;

    if (
      nextX > maximum ||
      nextX < -maximum ||
      nextY > maximum ||
      nextY < -maximum
    ) {
      gridGuide.dot.cx = previousX;
      gridGuide.dot.cy = previousY;
    }
  };

  const recordInteraction = (getter, interactionStartState) => {
    const leftIncompleteShape =
      gridGuide.grid &&
      gridGuide.grid.currentObj &&
      !gridGuide.grid.currentObj.second;

    if (leftIncompleteShape || !getter || !getter.recordGridInteraction) {
      return;
    }

    const changed = getter.recordCoalescedGridInteraction
      ? getter.recordCoalescedGridInteraction(
          interactionStartState,
          gridGuide.nudgeHistoryKey
        )
      : getter.recordGridInteraction(interactionStartState);

    if (changed) {
      changedMaybe();
    }
  };

  const performModalAction = (action, evt, isRepeating = false) => {
    if (action === 'done') {
      return closeFromModalInteraction();
    }

    if (
      !isRepeating &&
      evt &&
      canStartGridDragFromEvent(evt, { allowTouchInput: true })
    ) {
      startGridGuideArrowRepeat(action);
    }

    const previousX = gridGuide.dot.cx;
    const previousY = gridGuide.dot.cy;
    const getter = gridGuide.grid && gridGuide.grid.getter;
    const interactionStartState =
      getter && getter.cloneGridStateForHistory
        ? getter.cloneGridStateForHistory()
        : undefined;
    const needsAdjusting = nudgeDot(action);

    constrainDotToGrid(
      previousX,
      previousY,
      getGridGuideArrowVisibility(gridGuide.grid),
      needsAdjusting
    );

    recordInteraction(getter, interactionStartState);

    return true;
  };

  const startGridGuideArrowRepeat = action => {
    gridGuide.stopGridGuideArrowRepeat();
    arrowRepeatAction = action;

    arrowRepeater.start(() => {
      if (!gridGuide.on || arrowRepeatAction !== action || !gridGuide.dot) {
        gridGuide.stopGridGuideArrowRepeat();

        return false;
      }

      const previousX = gridGuide.dot.cx;
      const previousY = gridGuide.dot.cy;

      performModalAction(action, undefined, true);
      paintCanvas();

      const moved =
        gridGuide.dot &&
        (gridGuide.dot.cx !== previousX || gridGuide.dot.cy !== previousY);

      if (!moved) {
        gridGuide.stopGridGuideArrowRepeat();
        paintCanvas();
      }

      return moved;
    });
  };

  gridGuide.handlePointerDown = (x, y, evt) => {
    const layout = gridGuide.getModalLayout();
    const modalAction = getGridGuideModalActionAtPoint(layout, x, y);

    if (modalAction) {
      performModalAction(modalAction, evt);
    } else if (!isPointInGridGuideModal(layout, x, y)) {
      closeFromModalInteraction();
    }

    paintCanvas();

    return true;
  };

  gridGuide.paintMe = ctx => {
    const grid = gridGuide.grid;
    const gridLocation = getFullTopLeftLocation(grid);
    const pointLocation = grid.gridCoordinatesToPixelsFromTopLeftOfGrid(
      gridGuide.dot.cx,
      gridGuide.dot.cy
    );

    paintGridGuideModal({
      ctx,
      layout: gridGuide.getModalLayout(),
      grid: {
        x: gridLocation.x,
        y: gridLocation.y,
        w: grid.viewportW,
        h: grid.viewportH,
      },
      coordinatePlane: !isNumberLineGuide(),
      activeActions,
      themeColor: getThemeColor(),
      themeName: getThemeName(),
      point: {
        x: gridLocation.x + pointLocation.x,
        y: gridLocation.y + pointLocation.y,
        color: pointColor,
      },
    });
  };

  return gridGuide;
};
